import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, RefreshCw, Trash2, Globe, Lock, Plus, Users, Pencil } from 'lucide-react';
import { customDashboardsAPI, type CustomDashboardRunFilters } from '../../../services/api';
import { DashboardBlockCard } from './DashboardBlocks';
import BlockEditorBar from './BlockEditorBar';
import AudienceModal from './AudienceModal';
import DashboardDetailsModal from './DashboardDetailsModal';
import { InlineLoader } from '../../loading';
import { formatIsoDay } from '../../../utils/dateHelpers';
import type { CustomDashboard, DashboardRunResult, ReportParameter } from './types';

/**
 * Compute a date parameter's default. Mirrors the server's `defaultValueFor` so the
 * control and the server agree on what "no value supplied" means — if they drifted,
 * the filter bar would show one date while the query used another.
 */
const defaultDateFor = (p: ReportParameter): string => {
  const now = new Date();
  const ist = new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
  let base = new Date(Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate()));
  switch (p.default_anchor) {
    case 'start_of_week': {
      const dow = base.getUTCDay();
      base.setUTCDate(base.getUTCDate() - (dow === 0 ? 6 : dow - 1));
      break;
    }
    case 'start_of_month':
      base = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), 1));
      break;
    case 'start_of_year':
      base = new Date(Date.UTC(base.getUTCFullYear(), 0, 1));
      break;
    default:
      break;
  }
  const offset = p.default_anchor ? (p.default_offset_days ?? 0) : (p.default_relative_days ?? 0);
  base.setUTCDate(base.getUTCDate() + offset);
  return base.toISOString().slice(0, 10);
};

/**
 * Fill in the default of every parameter `values` has no entry for. Returns `values`
 * itself when there is nothing to add: a fresh object every time would change the
 * applied values, which rebuilds `execute`, which re-fires the run effect — an
 * infinite request loop.
 */
const withDefaults = (
  values: Record<string, string>,
  parameters: ReportParameter[] | undefined
): Record<string, string> => {
  let next = values;
  for (const p of parameters ?? []) {
    if (next[p.name] === undefined) {
      if (next === values) next = { ...values };
      next[p.name] = p.type === 'date' ? defaultDateFor(p) : (p.default_value ?? '');
    }
  }
  return next;
};

/** "From 1 Oct 2026 · To 3 Nov 2026": the values the numbers on screen were worked out for. */
const describeValues = (values: Record<string, string>, parameters: ReportParameter[]): string =>
  parameters
    .map((p) => `${p.label} ${p.type === 'date' ? formatIsoDay(values[p.name] ?? '') : (values[p.name] ?? '')}`.trim())
    .join(' · ');

const errorMessage = (e: unknown): string => {
  const status = (e as any)?.response?.status;
  const fromServer = (e as any)?.response?.data?.message;
  if (fromServer) return fromServer;
  // A bare 502/503/504 is the GATEWAY giving up, not our API answering. Generation
  // makes sequential model calls, so a long one can outlive the ingress timeout and
  // arrive as "Request failed with status code 503" — which tells the user nothing
  // and looks like a bug in their prompt.
  if (status === 502 || status === 503 || status === 504) {
    return 'The server took too long to answer and the request was cut off before it finished. This usually means the change was too large to work out in one go — try a smaller, more specific instruction.';
  }
  return (e as any)?.message || 'Something went wrong.';
};

export default function CustomDashboardView({
  dashboard: summary,
  canAuthor,
  onChanged,
  onDeleted,
  filters,
}: {
  /** The page's Filters window. Every block reads only the stores and people it keeps. */
  filters?: CustomDashboardRunFilters;
  /**
   * The LIST entry, which carries metadata only — `GET /custom-dashboards` returns
   * every dashboard with `blocks: []` so the tab strip does not pay for N block
   * queries it never renders. The full record, blocks included, is fetched below.
   */
  dashboard: CustomDashboard;
  canAuthor: boolean;
  onChanged: (next: CustomDashboard) => void;
  onDeleted: () => void;
}) {
  // Rendering straight from the list entry showed "This dashboard has no blocks"
  // for every dashboard, because the list never carries them.
  const [detail, setDetail] = useState<CustomDashboard>(summary);
  const dashboard = detail;

  const [run, setRun] = useState<DashboardRunResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The latest record, for changes built on it. A refine reports its block and then its
  // parameters in the same tick; built from the copy this render closed over, the second
  // change put the old block back.
  const detailRef = useRef(detail);
  detailRef.current = detail;

  /** Update local detail AND the parent's list entry (tab title / draft badge). */
  const applyChange = useCallback(
    (change: CustomDashboard | ((prev: CustomDashboard) => CustomDashboard)) => {
      const next = typeof change === 'function' ? change(detailRef.current) : change;
      detailRef.current = next;
      setDetail(next);
      onChanged(next);
    },
    [onChanged]
  );

  // Fetch the full record (blocks included) once per dashboard.
  useEffect(() => {
    let cancelled = false;
    customDashboardsAPI
      .get(summary.id)
      .then((res) => {
        if (!cancelled) setDetail(res.data.dashboard as CustomDashboard);
      })
      .catch((e) => {
        if (!cancelled) setError(errorMessage(e));
      });
    return () => { cancelled = true; };
  }, [summary.id]);

  // `values` are the APPLIED values, the ones runs use. `draft` is what the controls
  // show: a change waits for Apply. Running on every change gave no sign that a date
  // had been taken up, and a date typed digit by digit started a run per digit.
  const [values, setValues] = useState<Record<string, string>>(() => withDefaults({}, dashboard.parameters));
  const [draft, setDraft] = useState<Record<string, string>>(values);
  /** The values the run on screen was worked out for, so the bar can say so. */
  const [shownValues, setShownValues] = useState<Record<string, string> | null>(null);

  // A slow block action (Add a block, Refine) calls `execute` long after it started. The
  // run reads the dates and filters as they are NOW, or it would put back the ones from
  // before an Apply made in the meantime.
  const runInputs = useRef({ values, filters });
  runInputs.current = { values, filters };

  // Each filter change starts a run, and an earlier one can answer last: only the latest
  // run's answer is shown, so the numbers always match the filters on screen. `values`
  // and `filters` stay in the dependencies so a change still starts a run.
  const latestRun = useRef(0);
  const execute = useCallback(
    async (fresh = false) => {
      const runId = ++latestRun.current;
      const { values: params, filters: runFilters } = runInputs.current;
      setLoading(true);
      setError(null);
      try {
        const res = await customDashboardsAPI.run(dashboard.id, { params, fresh, ...(runFilters ? { filters: runFilters } : {}) });
        if (runId === latestRun.current) {
          setRun(res.data as DashboardRunResult);
          setShownValues(params);
        }
      } catch (e) {
        if (runId === latestRun.current) setError(errorMessage(e));
      } finally {
        if (runId === latestRun.current) setLoading(false);
      }
    },
    [dashboard.id, values, filters]
  );

  useEffect(() => {
    void execute(false);
  }, [execute]);

  const resultsByBlock = useMemo(() => {
    const map = new Map<number, DashboardRunResult['blocks'][number]>();
    for (const b of run?.blocks ?? []) map.set(b.block_id, b);
    return map;
  }, [run]);

  // Seed defaults for parameters that appear AFTER first render — a refine or an
  // added block can introduce one the dashboard has never had. Without this its
  // control renders blank while the server silently falls back to the declared
  // default, so the filter bar would show one thing and the query use another.
  useEffect(() => {
    setValues((prev) => withDefaults(prev, dashboard.parameters));
    setDraft((prev) => withDefaults(prev, dashboard.parameters));
  }, [dashboard.parameters]);

  /** A control was changed and not yet applied: the numbers below are for the old values. */
  const pending = (dashboard.parameters ?? []).some((p) => (draft[p.name] ?? '') !== (values[p.name] ?? ''));
  const apply = () => {
    // An emptied date means its default, as it does on the server. Put the default in the
    // control, so neither the control nor the "Showing" line is blank while the numbers
    // follow a date.
    const next = { ...draft };
    for (const p of dashboard.parameters ?? []) {
      if (p.type === 'date' && !next[p.name]) next[p.name] = defaultDateFor(p);
    }
    setDraft(next);
    // Nothing changed: Apply works the dashboard out again, as it always has.
    if ((dashboard.parameters ?? []).some((p) => (next[p.name] ?? '') !== (values[p.name] ?? ''))) setValues(next);
    else void execute(true);
  };

  // ── Authoring: reorder + add a block ──────────────────────────────────────
  const [audienceOpen, setAudienceOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [addingBlock, setAddingBlock] = useState(false);
  const [newBlockPrompt, setNewBlockPrompt] = useState('');
  const [newBlockType, setNewBlockType] = useState<'table' | 'chart' | 'kpi_card_row'>('table');
  const [addBusy, setAddBusy] = useState(false);

  const moveBlock = async (index: number, dir: -1 | 1) => {
    const ordered = [...dashboard.blocks].sort((a, b) => a.position - b.position);
    const target = index + dir;
    if (target < 0 || target >= ordered.length) return;
    // Captured BEFORE the optimistic update so the revert restores real positions
    // rather than the ones we just wrote.
    const previous = dashboard.blocks;
    [ordered[index], ordered[target]] = [ordered[target], ordered[index]];
    // Optimistic: renumber locally so the move is instant, then persist. The server
    // requires a full permutation of this dashboard's block ids.
    const renumbered = ordered.map((b, i) => ({ ...b, position: i }));
    applyChange({ ...dashboard, blocks: renumbered });
    try {
      await customDashboardsAPI.reorderBlocks(dashboard.id, renumbered.map((b) => b.id));
    } catch (e) {
      setError(errorMessage(e));
      applyChange({ ...dashboard, blocks: previous });
    }
  };

  const addBlock = async () => {
    const prompt = newBlockPrompt.trim();
    if (!prompt || addBusy) return;
    setAddBusy(true);
    setError(null);
    try {
      const res = await customDashboardsAPI.generateBlock(dashboard.id, {
        prompt,
        type: newBlockType,
        ...(newBlockType === 'chart' ? { chart_hint: 'bar' } : {}),
      });
      applyChange((prev) => ({
        ...prev,
        blocks: [...prev.blocks, res.data.block],
        ...(res.data.parameters ? { parameters: res.data.parameters } : {}),
      }));
      setNewBlockPrompt('');
      setAddingBlock(false);
      void execute(true);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setAddBusy(false);
    }
  };

  const togglePublish = async () => {
    setBusy(true);
    try {
      const res =
        dashboard.status === 'published'
          ? await customDashboardsAPI.unpublish(dashboard.id)
          : await customDashboardsAPI.publish(dashboard.id);
      applyChange(res.data.dashboard as CustomDashboard);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    // Deleting a published dashboard removes a tab other people are using, so it
    // asks first even though the author is entitled to do it.
    const msg =
      dashboard.status === 'published'
        ? `Delete “${dashboard.title}”? It is published, so it will disappear for everyone who can see it.`
        : `Delete “${dashboard.title}”?`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await customDashboardsAPI.remove(dashboard.id);
      onDeleted();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  // Blocks laid out in order. Two consecutive 'half' blocks share a row; everything
  // else spans. Grid placement (rather than flex wrapping) keeps a half block that
  // follows a full one from floating up beside it.
  const blocks = [...(dashboard.blocks ?? [])].sort((a, b) => a.position - b.position);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {dashboard.description && (
            <p className="text-sm text-gray-500">{dashboard.description}</p>
          )}
          <p className="mt-0.5 text-[11px] text-gray-400">
            {dashboard.status === 'published' ? 'Published' : 'Draft — only you can see this'}
            {run?.ranAt && ` · updated ${new Date(run.ranAt).toLocaleTimeString()}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => void execute(true)}
            disabled={loading}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {canAuthor && (
            <>
              <button
                type="button"
                onClick={togglePublish}
                disabled={busy}
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
              >
                {dashboard.status === 'published' ? (
                  <><Lock className="h-3.5 w-3.5" /> Unpublish</>
                ) : (
                  <><Globe className="h-3.5 w-3.5" /> Publish</>
                )}
              </button>
              <button
                type="button"
                onClick={() => setDetailsOpen(true)}
                disabled={busy}
                title="Rename this dashboard or edit its description"
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
              >
                <Pencil className="h-3.5 w-3.5" />
                Rename
              </button>
              <button
                type="button"
                onClick={() => setAudienceOpen(true)}
                disabled={busy}
                title="Choose which roles can see this dashboard"
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
              >
                <Users className="h-3.5 w-3.5" />
                {dashboard.audience_role_ids?.length
                  ? `${dashboard.audience_role_ids.length} role${dashboard.audience_role_ids.length === 1 ? '' : 's'}`
                  : 'Everyone'}
              </button>
              <button
                type="button"
                onClick={remove}
                disabled={busy}
                className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-white px-2.5 py-1.5 text-xs font-medium text-rose-700 transition hover:bg-rose-50 disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filter bar — one control per dashboard-level parameter. The same white card as
          the blocks under it: its faint grey tint only stood out on a white page, and the
          dashboards now sit on a grey one. */}
      {(dashboard.parameters ?? []).length > 0 && (
        <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-gray-200 bg-white px-3 py-2.5 shadow-sm">
          {dashboard.parameters.map((p) => (
            <div key={p.name}>
              <label className="block text-[11px] font-medium text-gray-500">{p.label}</label>
              {p.type === 'date' ? (
                <input
                  type="date"
                  value={draft[p.name] ?? ''}
                  onChange={(e) => setDraft((v) => ({ ...v, [p.name]: e.target.value }))}
                  className="mt-0.5 rounded-md border border-gray-200 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none"
                />
              ) : (
                <select
                  value={draft[p.name] ?? ''}
                  onChange={(e) => setDraft((v) => ({ ...v, [p.name]: e.target.value }))}
                  className="mt-0.5 rounded-md border border-gray-200 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none"
                >
                  {(run?.parameter_options?.[p.name]?.options ?? []).map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={apply}
            className="rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-primary-700"
          >
            Apply
          </button>
          {/* Says which values the numbers below belong to, and whether they are still
              being worked out — without it Apply looked like it did nothing. */}
          <div className="pb-1.5">
            {pending ? (
              <p className="text-xs font-medium text-amber-700">Not applied yet</p>
            ) : loading ? (
              <InlineLoader size="xs" label="Updating…" />
            ) : shownValues ? (
              <p className="text-xs text-gray-500">Showing {describeValues(shownValues, dashboard.parameters)}</p>
            ) : null}
          </div>
        </div>
      )}

      {/* Distinguishes "your team is empty" from "we couldn't work out your team" —
          without it a scoping failure looks like a dashboard that legitimately has
          no data, which is the more dangerous of the two to misread. */}
      {run?.viewerScopeUnavailable && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-100 bg-amber-50/70 px-3 py-2.5">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
          <p className="text-xs text-amber-800">
            We couldn’t confirm which employees you manage, so this dashboard is showing
            nothing rather than risk showing you someone else’s team. Try Refresh in a moment.
          </p>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-rose-100 bg-rose-50/70 px-3 py-2.5">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
          <p className="text-xs text-rose-800">{error}</p>
        </div>
      )}

      {/* The old numbers fade while new ones are worked out, so they are not read as the
          answer for values just applied. */}
      <div
        aria-busy={loading}
        className={`grid grid-cols-1 gap-4 lg:grid-cols-2 transition-opacity ${loading && run ? 'opacity-50' : ''}`}
      >
        {blocks.map((b, i) => (
          <div key={b.id} className={b.width === 'half' ? 'lg:col-span-1' : 'lg:col-span-2'}>
            <DashboardBlockCard
              block={b}
              result={resultsByBlock.get(b.id)}
              columnDisplay={run?.column_display ?? {}}
              onRetry={() => void execute(true)}
              footer={
                canAuthor ? (
                  <BlockEditorBar
                    dashboardId={dashboard.id}
                    block={b}
                    isFirst={i === 0}
                    isLast={i === blocks.length - 1}
                    onBlockChanged={(next) => {
                      applyChange((prev) => ({
                        ...prev,
                        blocks: prev.blocks.map((x) => (x.id === next.id ? next : x)),
                      }));
                      // The block's SQL or config changed, so its cached result is
                      // stale. `fresh` skips the server's per-block cache, which keys
                      // on updated_at and would otherwise still hold the old rows for
                      // up to 90s.
                      void execute(true);
                    }}
                    onParametersChanged={(params) =>
                      applyChange((prev) => ({ ...prev, parameters: params as typeof prev.parameters }))
                    }
                    onMove={(dir) => void moveBlock(i, dir)}
                    onDeleted={() => {
                      applyChange((prev) => ({
                        ...prev,
                        blocks: prev.blocks.filter((x) => x.id !== b.id),
                      }));
                      void execute(true);
                    }}
                  />
                ) : undefined
              }
            />
          </div>
        ))}
      </div>

      {blocks.length === 0 && (
        <p className="py-8 text-center text-sm text-gray-400">This dashboard has no blocks.</p>
      )}

      {detailsOpen && (
        <DashboardDetailsModal
          dashboard={dashboard}
          onClose={() => setDetailsOpen(false)}
          onSaved={applyChange}
        />
      )}

      {audienceOpen && (
        <AudienceModal
          dashboard={dashboard}
          projectId={dashboard.project_id}
          onClose={() => setAudienceOpen(false)}
          onSaved={applyChange}
        />
      )}

      {canAuthor && (
        <div className="rounded-xl border border-dashed border-gray-200 px-4 py-3">
          {addingBlock ? (
            <div className="flex flex-wrap items-start gap-2">
              <select
                value={newBlockType}
                onChange={(e) => setNewBlockType(e.target.value as typeof newBlockType)}
                disabled={addBusy}
                className="rounded-md border border-gray-200 px-2 py-1.5 text-xs focus:border-primary-500 focus:outline-none"
              >
                <option value="table">Table</option>
                <option value="chart">Chart</option>
                <option value="kpi_card_row">KPI cards</option>
              </select>
              <input
                value={newBlockPrompt}
                onChange={(e) => setNewBlockPrompt(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void addBlock(); }}
                disabled={addBusy}
                autoFocus
                placeholder="Describe the block — e.g. red flags by type this month"
                className="min-w-[240px] flex-1 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs focus:border-primary-500 focus:outline-none disabled:bg-gray-50"
              />
              <button
                type="button"
                onClick={() => void addBlock()}
                disabled={addBusy || !newBlockPrompt.trim()}
                className="rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-primary-700 disabled:opacity-50"
              >
                {addBusy ? 'Building…' : 'Add'}
              </button>
              <button
                type="button"
                onClick={() => setAddingBlock(false)}
                disabled={addBusy}
                className="rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 transition hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAddingBlock(true)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 transition hover:text-primary-600"
            >
              <Plus className="h-3.5 w-3.5" />
              Add a block
            </button>
          )}
        </div>
      )}
    </div>
  );
}
